import { useState, useEffect, useCallback } from 'react';
import { Loader2, CheckCircle2, XCircle, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { timesheetsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const ROW_STATUS = {
  matched: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  swap: 'bg-blue-50 text-blue-700 border-blue-200',
  unscheduled: 'bg-slate-100 text-slate-600 border-slate-200',
  same_name_warning: 'bg-amber-50 text-amber-700 border-amber-200',
  error: 'bg-red-50 text-red-700 border-red-200',
};

export function TimesheetApproval({ job, weekStart, open, onClose, onDone }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [review, setReview] = useState(null);
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null); // null | 'reject' | 'self-approve'
  const [reason, setReason] = useState('');
  const [ack, setAck] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rv, st] = await Promise.all([
        timesheetsAPI.review(job.job_id, weekStart),
        timesheetsAPI.state(job.job_id, weekStart),
      ]);
      setReview(rv.data);
      setState(st.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load timesheet');
    } finally {
      setLoading(false);
    }
  }, [job, weekStart]);

  useEffect(() => { if (open) { setMode(null); setReason(''); setAck(false); load(); } }, [open, load]);

  const close = () => { onClose(); };
  const isSelf = state && user && state.submitted_by === user.id;

  const doApprove = async () => {
    setBusy(true);
    try {
      const res = await timesheetsAPI.approve(job.job_id, weekStart, review.version_id);
      setState(res.data);
      toast.success('Timesheet approved');
      onDone && onDone();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    } finally {
      setBusy(false);
    }
  };

  const onApproveClick = () => { doApprove(); };

  const doReject = async () => {
    if (reason.trim().length < 10) { toast.error('A rejection reason needs at least 10 characters'); return; }
    setBusy(true);
    try {
      const res = await timesheetsAPI.reject(job.job_id, weekStart, review.version_id, reason.trim());
      setState(res.data);
      toast.success('Timesheet returned to the uploader');
      onDone && onDone();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    } finally {
      setBusy(false);
    }
  };

  const rows = review?.rows || [];
  const status = state?.status;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="timesheet-approval-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Review timesheet — #{job?.job_number} · week of {weekStart}
          </DialogTitle>
          <DialogDescription data-testid="approval-status">
            Status: <strong>{status || '—'}</strong>
            {status === 'approved' && state?.approved_by_email && <> · approved by {state.approved_by_email}</>}
            {status === 'rejected' && state?.reject_reason && <> · returned: {state.reject_reason}</>}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="p-10 flex items-center justify-center text-slate-400" data-testid="approval-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="space-y-4">
            <div className="border rounded-md overflow-hidden" data-testid="approval-rows">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="p-2 text-left">Status</th>
                    <th className="p-2 text-left">Employee</th>
                    <th className="p-2 text-left">Date / Post</th>
                    <th className="p-2 text-left">Times · Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className={`border-t align-top ${r.excluded ? 'opacity-40' : ''}`} data-testid={`approval-row-${r.id}`}>
                      <td className="p-2">
                        <Badge variant="outline" className={ROW_STATUS[r.status] || ROW_STATUS.error}>{r.status}</Badge>
                      </td>
                      <td className="p-2">
                        <div className="font-medium">{r.employee_name || '—'}</div>
                        <div className="text-xs text-slate-500">#{r.employee_number || '—'}</div>
                      </td>
                      <td className="p-2">
                        <div>{r.date}</div>
                        <div className="text-xs text-slate-500">{r.post}</div>
                      </td>
                      <td className="p-2">
                        <div>{r.time_in}–{r.time_out}{r.next_day ? ' (+1d)' : ''}</div>
                        <div className="text-xs text-slate-500">{r.computed_hours ?? '—'} h</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {mode === 'reject' && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 space-y-2" data-testid="reject-panel">
                <Label className="text-sm text-red-800">Reason the uploader needs to fix (min 10 characters)</Label>
                <Textarea placeholder="e.g. Gate post hours on Tue look doubled — please re-check" value={reason}
                          onChange={(e) => setReason(e.target.value)} data-testid="reject-reason" />
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {status === 'submitted' && !loading ? (
            <div className="flex gap-2 w-full justify-end">
              {mode === 'reject' ? (
                <>
                  <Button variant="outline" onClick={() => { setMode(null); setReason(''); }} data-testid="reject-cancel-btn">Cancel</Button>
                  <Button variant="destructive" onClick={doReject} disabled={busy} data-testid="reject-confirm-btn">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                    Return to uploader
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setMode('reject')} data-testid="reject-btn">
                    <XCircle className="w-4 h-4 mr-2" /> Reject
                  </Button>
                  <Button onClick={onApproveClick} disabled={busy} data-testid="approve-btn">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    Approve
                  </Button>
                </>
              )}
            </div>
          ) : (
            !loading && (
              <Button variant="outline" onClick={close} data-testid="approval-close-btn">Close</Button>
            )
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
